import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Camera, Trash2, Calendar, ChevronLeft, ChevronRight, X, ZoomIn, Plus, GitCompare, Check, Share2, Facebook, Instagram, ExternalLink, Cloud, CloudOff, Loader2, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { trackUsage, FEATURES } from '../services/usageTracking';

const STORAGE_KEY = 'gym_notebook_progress_photos';
const MAX_PHOTOS = 50; // Limit to prevent localStorage overflow

export default function ProgressPhotos({ onBack, isExpired, onNavigate }) {
  const [photos, setPhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoNote, setPhotoNote] = useState('');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  // Compare mode state
  const [compareMode, setCompareMode] = useState(false);
  const [comparePhotos, setComparePhotos] = useState({ before: null, after: null });
  const [showCompareView, setShowCompareView] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(50);
  const compareRef = useRef(null);
  
  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [photoToShare, setPhotoToShare] = useState(null);
  
  // Camera state
  const [showCameraChoice, setShowCameraChoice] = useState(false);
  
  // Track usage on mount
  useEffect(() => {
    trackUsage(FEATURES.PHOTOS);
  }, []);

  // Social media links
  const socialMediaLinks = {
    facebook: 'https://facebook.com',
    instagram: 'https://instagram.com',
    tiktok: 'https://tiktok.com',
    snapchat: 'https://snapchat.com'
  };

  // Share photo function
  const sharePhoto = async (platform) => {
    if (!photoToShare) return;
    
    const shareText = `Check out my health progress! 💪 #WalkingNutrition #HealthJourney #ProgressPhotos`;
    const shareUrl = 'https://nutristeps.me';
    
    // For Web Share API (mobile)
    if (platform === 'native' && navigator.share) {
      try {
        // Convert base64 to blob for sharing
        const response = await fetch(photoToShare.data);
        const blob = await response.blob();
        const file = new File([blob], 'progress-photo.jpg', { type: 'image/jpeg' });
        
        await navigator.share({
          title: 'My Fitness Progress',
          text: shareText,
          files: [file],
          url: shareUrl
        });
        toast.success('Shared successfully!');
        setShowShareModal(false);
        return;
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    }
    
    // Platform-specific sharing
    let shareLink = '';
    switch (platform) {
      case 'facebook':
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
        break;
      case 'twitter':
        shareLink = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'whatsapp':
        shareLink = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
        break;
      default:
        // Open social media page for Instagram, TikTok, Snapchat (they don't support direct web sharing)
        shareLink = socialMediaLinks[platform];
    }
    
    if (shareLink) {
      window.open(shareLink, '_blank', 'width=600,height=400');
      toast.success(`Opening ${platform}...`);
    }
    
    setShowShareModal(false);
  };

  // Download photo for manual sharing
  const downloadPhoto = () => {
    if (!photoToShare) return;
    
    const link = document.createElement('a');
    link.href = photoToShare.data;
    link.download = `fitness-progress-${new Date(photoToShare.date).toISOString().split('T')[0]}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Photo downloaded! You can now share it on any platform.');
    setShowShareModal(false);
  };

  // Open share modal
  const openShareModal = (photo) => {
    setPhotoToShare(photo);
    setShowShareModal(true);
  };

  const [isLoading, setIsLoading] = useState(true);

  // Load photos from S3/backend
  useEffect(() => {
    const loadPhotos = async () => {
      setIsLoading(true);
      try {
        // Try to load from S3/backend first
        const response = await api.get('/photos');
        if (response?.photos && response.photos.length > 0) {
          // Transform to match expected format
          const cloudPhotos = response.photos.map(p => ({
            id: p.photo_id,
            data: p.url || p.s3_url || p.image, // Use url, s3_url or image fallback
            url: p.url || p.s3_url || p.image,
            date: p.date,
            note: p.note || p.notes || ''
          }));
          setPhotos(cloudPhotos);
          console.log('[ProgressPhotos] Loaded from cloud:', cloudPhotos.length, 'photos');
        } else {
          // Fall back to localStorage for old photos
          const saved = localStorage.getItem(STORAGE_KEY);
          if (saved) {
            setPhotos(JSON.parse(saved));
          }
        }
      } catch (e) {
        console.log('[ProgressPhotos] Cloud load failed, using localStorage:', e.message);
        // Fall back to localStorage
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          try {
            setPhotos(JSON.parse(saved));
          } catch (parseErr) {
            console.error('Error parsing saved photos:', parseErr);
          }
        }
      }
      setIsLoading(false);
    };
    
    loadPhotos();
  }, []);

  // Save photo to S3
  const savePhotoToCloud = async (imageData, note) => {
    try {
      console.log('[ProgressPhotos] Uploading to S3...');
      const response = await api.post('/photos/upload', {
        image_data: imageData,
        note: note || '',
        date: new Date().toISOString()
      });
      console.log('[ProgressPhotos] S3 upload success:', response);
      return response;
    } catch (e) {
      console.error('[ProgressPhotos] S3 upload failed:', e);
      toast.error('Cloud upload failed: ' + (e.message || 'Unknown error'));
      throw e;
    }
  };

  // Delete photo from S3
  const deletePhotoFromCloud = async (photoId) => {
    try {
      await api.delete(`/photos/${photoId}`);
    } catch (e) {
      console.error('[ProgressPhotos] S3 delete failed:', e.message);
    }
  };

  // Compress image to reduce storage size
  const compressImage = (file, maxWidth = 800, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          // Fill white background to prevent dark/black areas in JPEG output
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
          
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.onerror = reject;
        img.src = e.target?.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Handle file selection
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (photos.length >= MAX_PHOTOS) {
      toast.error(`Maximum ${MAX_PHOTOS} photos allowed. Delete some to add more.`);
      return;
    }

    setUploading(true);
    try {
      const compressedImage = await compressImage(file);
      
      // Upload to S3
      const response = await savePhotoToCloud(compressedImage, photoNote);
      
      const newPhoto = {
        id: response.photo_id,
        data: response.url || response.s3_url || compressedImage, // Use url from response
        url: response.url || response.s3_url || compressedImage,
        date: response.date || new Date().toISOString(),
        note: response.note || photoNote,
      };

      setPhotos(prevPhotos => [newPhoto, ...prevPhotos]);
      
      setShowUploadModal(false);
      setPhotoNote('');
      toast.success('Photo uploaded to cloud!');
    } catch (error) {
      console.error('Image upload error:', error);
      // Fall back to localStorage if S3 fails
      toast.warning('Cloud upload failed, saving locally...');
      try {
        const compressedImage = await compressImage(file);
        const newPhoto = {
          id: Date.now(),
          data: compressedImage,
          date: new Date().toISOString(),
          note: photoNote,
        };
        const newPhotos = [newPhoto, ...photos];
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newPhotos));
        setPhotos(newPhotos);
        setShowUploadModal(false);
        setPhotoNote('');
        toast.info('Photo saved locally (cloud unavailable)');
      } catch (localError) {
        console.error('Local save error:', localError);
        toast.error('Failed to save photo: ' + localError.message);
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Delete a photo
  const deletePhoto = async (photoId) => {
    if (window.confirm('Delete this photo?')) {
      // Delete from cloud
      await deletePhotoFromCloud(photoId);
      
      // Remove from state
      const newPhotos = photos.filter(p => p.id !== photoId);
      setPhotos(newPhotos);
      setSelectedPhoto(null);
      toast.success('Photo deleted');
    }
  };

  // Handle photo click in compare mode
  const handlePhotoClick = (photo) => {
    if (compareMode) {
      if (!comparePhotos.before) {
        setComparePhotos({ ...comparePhotos, before: photo });
        toast.info('Select "After" photo');
      } else if (!comparePhotos.after && photo.id !== comparePhotos.before.id) {
        setComparePhotos({ ...comparePhotos, after: photo });
        setShowCompareView(true);
        setCompareMode(false);
      } else if (photo.id === comparePhotos.before.id) {
        // Deselect
        setComparePhotos({ ...comparePhotos, before: null });
      }
    } else {
      setSelectedPhoto(photo);
    }
  };

  // Reset compare mode
  const resetCompare = () => {
    setCompareMode(false);
    setComparePhotos({ before: null, after: null });
    setShowCompareView(false);
    setSliderPosition(50);
  };

  // Handle slider drag
  const handleSliderDrag = (e) => {
    if (!compareRef.current) return;
    const rect = compareRef.current.getBoundingClientRect();
    const x = e.clientX || e.touches?.[0]?.clientX;
    const position = ((x - rect.left) / rect.width) * 100;
    setSliderPosition(Math.max(0, Math.min(100, position)));
  };

  // Group photos by month
  const groupPhotosByMonth = () => {
    const groups = {};
    photos.forEach(photo => {
      const date = new Date(photo.date);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      if (!groups[key]) {
        groups[key] = { label, photos: [] };
      }
      groups[key].photos.push(photo);
    });
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  };

  const photoGroups = groupPhotosByMonth();

  // Navigate between photos
  const navigatePhoto = (direction) => {
    if (!selectedPhoto) return;
    const currentIndex = photos.findIndex(p => p.id === selectedPhoto.id);
    const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex >= 0 && newIndex < photos.length) {
      setSelectedPhoto(photos[newIndex]);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Uploading Overlay */}
      {uploading && (
        <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center">
          <div className="bg-slate-900 rounded-2xl p-6 flex flex-col items-center gap-4 border border-slate-800">
            <Loader2 className="h-12 w-12 text-pink-400 animate-spin" />
            <p className="text-white text-lg font-heading font-medium">Uploading photo...</p>
            <p className="text-slate-400 text-sm">Please wait</p>
          </div>
        </div>
      )}
      
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950 border-none ios-header" style={{ paddingTop: 'max(16px, env(safe-area-inset-top, 16px))', border: 'none' }}>
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center shadow-lg shadow-pink-500/20">
              <Camera className="h-5 w-5 text-white" />
            </div>
            <h1 className="text-base font-heading font-bold text-white">
              {compareMode ? 'Compare Photos' : 'Progress Photos'}
            </h1>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setShowOnboarding(true)} 
              className="text-slate-400 hover:text-pink-400"
              title="Help & Tutorial"
            >
              <HelpCircle className="h-4 w-4" />
            </Button>
            {photos.length >= 2 && !compareMode && (
              <Button
                onClick={() => {
                  setCompareMode(true);
                  setComparePhotos({ before: null, after: null });
                  toast.info('Select "Before" photo first');
                }}
                variant="outline"
                className="border-violet-500/50 text-violet-400 hover:bg-violet-500/20 rounded-xl"
                data-testid="compare-btn"
              >
                <GitCompare className="h-4 w-4 mr-2" />
                Compare
              </Button>
            )}
            {compareMode && (
              <Button
                onClick={resetCompare}
                variant="outline"
                className="border-slate-500 text-slate-400"
              >
                Cancel
              </Button>
            )}
            <Button
              onClick={() => setShowCameraChoice(true)}
              className="bg-pink-500 hover:bg-pink-600 text-white"
              data-testid="add-photo-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Photo
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 pb-36">
        <div className="max-w-lg mx-auto">
          {photos.length === 0 ? (
            /* Empty State */
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-12 text-center">
                <Camera className="h-16 w-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">No Photos Yet</h3>
                <p className="text-slate-400 mb-6">
                  Track your health journey by adding progress photos.
                </p>
                <Button
                  onClick={() => setShowCameraChoice(true)}
                  className="bg-pink-500 hover:bg-pink-600 text-white"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Add Your First Photo
                </Button>
              </CardContent>
            </Card>
          ) : (
            /* Photo Gallery */
            <div className="space-y-8">
              {/* Compare mode instructions */}
              {compareMode && (
                <div className="bg-purple-500/20 border border-purple-500/30 rounded-lg p-4 text-center">
                  <p className="text-purple-300">
                    {!comparePhotos.before 
                      ? '👆 Select your "BEFORE" photo first'
                      : '👆 Now select your "AFTER" photo'}
                  </p>
                </div>
              )}
              
              {photoGroups.map(([key, group]) => (
                <div key={key}>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-pink-400" />
                    {group.label}
                    <span className="text-sm text-slate-400 font-normal">
                      ({group.photos.length} {group.photos.length === 1 ? 'photo' : 'photos'})
                    </span>
                  </h3>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {group.photos.map(photo => {
                      const isSelectedBefore = comparePhotos.before?.id === photo.id;
                      const isSelectedAfter = comparePhotos.after?.id === photo.id;
                      
                      return (
                        <button
                          key={photo.id}
                          onClick={() => handlePhotoClick(photo)}
                          className={`relative aspect-square rounded-xl overflow-hidden bg-slate-700 transition-all group ${
                            isSelectedBefore 
                              ? 'ring-4 ring-yellow-500 scale-95' 
                              : isSelectedAfter 
                                ? 'ring-4 ring-green-500 scale-95'
                                : compareMode 
                                  ? 'hover:ring-2 hover:ring-purple-500'
                                  : 'hover:ring-2 hover:ring-pink-500'
                          }`}
                          data-testid={`photo-${photo.id}`}
                        >
                          {(photo.data || photo.url) ? (
                            <img
                              src={photo.data || photo.url}
                              alt={`Progress ${new Date(photo.date).toLocaleDateString()}`}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              style={{ backgroundColor: '#1e293b' }}
                              onLoad={(e) => {
                                // Detect 1x1 pixel placeholder images (corrupted data)
                                if (e.target.naturalWidth <= 1 || e.target.naturalHeight <= 1) {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling?.classList?.remove('hidden');
                                } else {
                                  e.target.style.backgroundColor = 'transparent';
                                }
                              }}
                              onError={(e) => {
                                console.log('Photo load error:', photo.id, photo.url || photo.data?.substring(0, 50));
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                                e.target.nextSibling?.classList?.remove('hidden');
                              }}
                            />
                          ) : null}
                          <div className={`w-full h-full bg-slate-700 flex flex-col items-center justify-center absolute inset-0 ${(photo.data || photo.url) ? 'hidden' : ''}`}>
                            <Camera className="h-8 w-8 text-slate-500 mb-1" />
                            <span className="text-slate-500 text-[10px]">Unavailable</span>
                          </div>
                          
                          {/* Selection indicator for compare mode */}
                          {(isSelectedBefore || isSelectedAfter) && (
                            <div className={`absolute top-2 left-2 px-2 py-1 rounded text-xs font-bold ${
                              isSelectedBefore ? 'bg-yellow-500 text-slate-900' : 'bg-green-500 text-white'
                            }`}>
                              {isSelectedBefore ? 'BEFORE' : 'AFTER'}
                            </div>
                          )}
                          
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                            <span className="text-xs text-white">
                              {new Date(photo.date).toLocaleDateString()}
                            </span>
                          </div>
                          {!compareMode && (
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <ZoomIn className="h-5 w-5 text-white drop-shadow-lg" />
                            </div>
                          )}
                          {compareMode && !isSelectedBefore && !isSelectedAfter && (
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Check className="h-5 w-5 text-purple-400 drop-shadow-lg" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Photo count */}
          {photos.length > 0 && !compareMode && (
            <p className="text-center text-sm text-slate-500 mt-8">
              {photos.length} / {MAX_PHOTOS} photos
            </p>
          )}

          {/* Social Media Section */}
          <div className="mt-12 pt-8 border-t border-slate-700">
            <p className="text-center text-slate-400 text-sm mb-4">
              Share your health journey on social media
            </p>
            <div className="flex justify-center gap-4">
              {/* Facebook */}
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-[#1877F2] flex items-center justify-center hover:scale-110 transition-transform shadow-lg"
                data-testid="social-facebook"
              >
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              
              {/* Instagram */}
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] flex items-center justify-center hover:scale-110 transition-transform shadow-lg"
                data-testid="social-instagram"
              >
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
              </a>
              
              {/* TikTok */}
              <a
                href="https://tiktok.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-black flex items-center justify-center hover:scale-110 transition-transform shadow-lg border border-slate-600"
                data-testid="social-tiktok"
              >
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                </svg>
              </a>
              
              {/* Snapchat */}
              <a
                href="https://snapchat.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-[#FFFC00] flex items-center justify-center hover:scale-110 transition-transform shadow-lg"
                data-testid="social-snapchat"
              >
                <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301a.603.603 0 01.257-.053c.21 0 .42.089.57.254.104.12.15.255.15.39a.756.756 0 01-.401.645c-.062.03-1.182.594-2.056.702a.615.615 0 00-.541.645c-.001.06.006.121.02.18.253.92.538 1.537 1.069 2.004.225.197.438.36.654.503.46.3 1.26.78 1.35 1.47.015.09.02.18.01.27-.03.18-.06.33-.13.44-.15.255-.345.42-.57.51a3.01 3.01 0 01-1.08.225c-.27.015-.54.015-.81 0a5.06 5.06 0 00-.84.075c-.18.03-.33.06-.45.09-.24.06-.39.12-.57.195a8.106 8.106 0 01-3.435 1.26c-.03 0-.06 0-.09.004-.06.01-.12.01-.18.01-.06 0-.12 0-.18-.01-.03-.004-.06-.004-.09-.004a8.106 8.106 0 01-3.435-1.26 3.11 3.11 0 00-.57-.195c-.12-.03-.27-.06-.45-.09a5.06 5.06 0 00-.84-.075c-.27.015-.54.015-.81 0a3.01 3.01 0 01-1.08-.225c-.225-.09-.42-.255-.57-.51a1.26 1.26 0 01-.13-.44c-.01-.09-.005-.18.01-.27.09-.69.89-1.17 1.35-1.47.216-.143.429-.306.654-.503.531-.467.816-1.084 1.069-2.004a.671.671 0 00.02-.18.615.615 0 00-.541-.645c-.874-.108-1.994-.672-2.056-.702a.756.756 0 01-.401-.645c0-.135.046-.27.15-.39a.714.714 0 01.57-.254c.086 0 .171.017.257.053.374.181.733.285 1.033.301.198 0 .326-.045.401-.09a6.44 6.44 0 01-.03-.51l-.003-.06c-.104-1.628-.23-3.654.299-4.847C7.859 1.069 11.216.793 12.206.793z"/>
                </svg>
              </a>
            </div>
            
            {/* Hashtag suggestion */}
            <p className="text-center text-slate-500 text-xs mt-4">
              Use <span className="text-pink-400">#WalkingNutrition</span> <span className="text-purple-400">#ProgressPhotos</span> <span className="text-blue-400">#HealthJourney</span>
            </p>
          </div>
        </div>
      </main>

      {/* Camera Choice Modal */}
      <Dialog open={showCameraChoice} onOpenChange={setShowCameraChoice}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Camera className="h-5 w-5 text-pink-400" />
              Add Progress Photo
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 pt-4">
            {/* Take Selfie / Photo */}
            <button
              onClick={() => {
                setShowCameraChoice(false);
                // Small delay to ensure modal closes before camera opens
                setTimeout(() => {
                  cameraInputRef.current?.click();
                }, 100);
              }}
              className="w-full flex items-center gap-4 p-4 bg-gradient-to-r from-pink-500/20 to-purple-500/20 hover:from-pink-500/30 hover:to-purple-500/30 rounded-xl border border-pink-500/30 transition-all"
              data-testid="take-photo-btn"
            >
              <div className="w-12 h-12 rounded-full bg-pink-500 flex items-center justify-center">
                <Camera className="h-6 w-6 text-white" />
              </div>
              <div className="text-left">
                <p className="text-white font-semibold">Take Photo / Selfie</p>
                <p className="text-slate-400 text-sm">Use your camera now</p>
              </div>
            </button>
            
            {/* Choose from Gallery */}
            <button
              onClick={() => {
                setShowCameraChoice(false);
                setShowUploadModal(true);
              }}
              className="w-full flex items-center gap-4 p-4 bg-slate-700/50 hover:bg-slate-700 rounded-xl border border-slate-600 transition-all"
              data-testid="choose-gallery-btn"
            >
              <div className="w-12 h-12 rounded-full bg-slate-600 flex items-center justify-center">
                <Plus className="h-6 w-6 text-white" />
              </div>
              <div className="text-left">
                <p className="text-white font-semibold">Choose from Gallery</p>
                <p className="text-slate-400 text-sm">Upload existing photo</p>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Hidden camera input - moved outside dialog */}
      <input
        type="file"
        accept="image/*"
        capture="user"
        onChange={handleFileSelect}
        ref={cameraInputRef}
        className="hidden"
        data-testid="camera-input"
      />

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Camera className="h-5 w-5 text-pink-400" />
              Add Progress Photo
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              ref={fileInputRef}
              className="hidden"
              data-testid="photo-file-input"
            />
            
            <div
              onClick={() => !uploading && fileInputRef.current?.click()}
              className="border-2 border-dashed border-slate-600 rounded-xl p-8 text-center cursor-pointer hover:border-pink-500 transition-colors"
            >
              {uploading ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="w-8 h-8 border-2 border-pink-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-slate-400">Processing...</span>
                </div>
              ) : (
                <>
                  <Camera className="h-12 w-12 text-slate-500 mx-auto mb-2" />
                  <p className="text-slate-400">Tap to select a photo</p>
                  <p className="text-xs text-slate-500 mt-1">JPG, PNG up to 5MB</p>
                </>
              )}
            </div>
            
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Note (optional)</label>
              <input
                type="text"
                value={photoNote}
                onChange={(e) => setPhotoNote(e.target.value)}
                placeholder="e.g., Week 4, after leg day"
                className="w-full bg-slate-700 border-slate-600 text-white rounded-lg p-2"
                data-testid="photo-note-input"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Viewer Modal */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-4xl p-0 overflow-hidden">
          {selectedPhoto && (
            <div className="relative">
              {/* Navigation buttons */}
              <button
                onClick={() => navigatePhoto('prev')}
                disabled={photos.findIndex(p => p.id === selectedPhoto.id) === 0}
                className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-2 bg-black/50 rounded-full text-white disabled:opacity-30 hover:bg-black/70"
                data-testid="prev-photo-btn"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              
              <button
                onClick={() => navigatePhoto('next')}
                disabled={photos.findIndex(p => p.id === selectedPhoto.id) === photos.length - 1}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-2 bg-black/50 rounded-full text-white disabled:opacity-30 hover:bg-black/70"
                data-testid="next-photo-btn"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
              
              {/* Close button */}
              <button
                onClick={() => setSelectedPhoto(null)}
                className="absolute top-2 right-2 z-10 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
              >
                <X className="h-5 w-5" />
              </button>
              
              {/* Image */}
              <img
                src={selectedPhoto.data}
                alt="Progress photo"
                className="w-full max-h-[70vh] object-contain bg-black"
              />
              
              {/* Info bar */}
              <div className="p-4 bg-slate-800 flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">
                    {new Date(selectedPhoto.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                  {selectedPhoto.note && (
                    <p className="text-slate-400 text-sm">{selectedPhoto.note}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => openShareModal(selectedPhoto)}
                    variant="ghost"
                    className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/20"
                    data-testid="share-photo-btn"
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Share
                  </Button>
                  <Button
                    onClick={() => deletePhoto(selectedPhoto.id)}
                    variant="ghost"
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                    data-testid="delete-photo-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Share Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Share2 className="h-5 w-5 text-blue-400" />
              Share Progress Photo
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            {/* Preview */}
            {photoToShare && (
              <div className="aspect-square w-32 mx-auto rounded-lg overflow-hidden bg-slate-700">
                <img 
                  src={photoToShare.data} 
                  alt="Photo to share" 
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            
            {/* Native Share (Mobile) */}
            {navigator.share && (
              <Button
                onClick={() => sharePhoto('native')}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                data-testid="share-native-btn"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share via Device
              </Button>
            )}
            
            {/* Social Media Grid */}
            <div className="grid grid-cols-2 gap-3">
              {/* Facebook */}
              <button
                onClick={() => sharePhoto('facebook')}
                className="flex items-center gap-3 p-3 bg-[#1877F2]/20 hover:bg-[#1877F2]/30 rounded-xl transition-colors"
                data-testid="share-facebook-btn"
              >
                <div className="w-10 h-10 rounded-full bg-[#1877F2] flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </div>
                <span className="text-white text-sm font-medium">Facebook</span>
              </button>
              
              {/* Instagram */}
              <button
                onClick={() => sharePhoto('instagram')}
                className="flex items-center gap-3 p-3 bg-gradient-to-br from-[#833AB4]/20 via-[#FD1D1D]/20 to-[#F77737]/20 hover:from-[#833AB4]/30 hover:via-[#FD1D1D]/30 hover:to-[#F77737]/30 rounded-xl transition-colors"
                data-testid="share-instagram-btn"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                  </svg>
                </div>
                <span className="text-white text-sm font-medium">Instagram</span>
              </button>
              
              {/* TikTok */}
              <button
                onClick={() => sharePhoto('tiktok')}
                className="flex items-center gap-3 p-3 bg-black/30 hover:bg-black/50 rounded-xl transition-colors"
                data-testid="share-tiktok-btn"
              >
                <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                  </svg>
                </div>
                <span className="text-white text-sm font-medium">TikTok</span>
              </button>
              
              {/* Snapchat */}
              <button
                onClick={() => sharePhoto('snapchat')}
                className="flex items-center gap-3 p-3 bg-[#FFFC00]/20 hover:bg-[#FFFC00]/30 rounded-xl transition-colors"
                data-testid="share-snapchat-btn"
              >
                <div className="w-10 h-10 rounded-full bg-[#FFFC00] flex items-center justify-center">
                  <svg className="w-5 h-5 text-black" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301a.603.603 0 01.257-.053c.21 0 .42.089.57.254.104.12.15.255.15.39a.756.756 0 01-.401.645c-.062.03-1.182.594-2.056.702a.615.615 0 00-.541.645c-.001.06.006.121.02.18.253.92.538 1.537 1.069 2.004.225.197.438.36.654.503.46.3 1.26.78 1.35 1.47.015.09.02.18.01.27-.03.18-.06.33-.13.44-.15.255-.345.42-.57.51a3.01 3.01 0 01-1.08.225c-.27.015-.54.015-.81 0a5.06 5.06 0 00-.84.075c-.18.03-.33.06-.45.09-.24.06-.39.12-.57.195a8.106 8.106 0 01-3.435 1.26c-.03 0-.06 0-.09.004-.06.01-.12.01-.18.01-.06 0-.12 0-.18-.01-.03-.004-.06-.004-.09-.004a8.106 8.106 0 01-3.435-1.26 3.11 3.11 0 00-.57-.195c-.12-.03-.27-.06-.45-.09a5.06 5.06 0 00-.84-.075c-.27.015-.54.015-.81 0a3.01 3.01 0 01-1.08-.225c-.225-.09-.42-.255-.57-.51a1.26 1.26 0 01-.13-.44c-.01-.09-.005-.18.01-.27.09-.69.89-1.17 1.35-1.47.216-.143.429-.306.654-.503.531-.467.816-1.084 1.069-2.004a.671.671 0 00.02-.18.615.615 0 00-.541-.645c-.874-.108-1.994-.672-2.056-.702a.756.756 0 01-.401-.645c0-.135.046-.27.15-.39a.714.714 0 01.57-.254c.086 0 .171.017.257.053.374.181.733.285 1.033.301.198 0 .326-.045.401-.09a6.44 6.44 0 01-.03-.51l-.003-.06c-.104-1.628-.23-3.654.299-4.847C7.859 1.069 11.216.793 12.206.793z"/>
                  </svg>
                </div>
                <span className="text-white text-sm font-medium">Snapchat</span>
              </button>
            </div>
            
            {/* WhatsApp */}
            <button
              onClick={() => sharePhoto('whatsapp')}
              className="w-full flex items-center justify-center gap-3 p-3 bg-[#25D366]/20 hover:bg-[#25D366]/30 rounded-xl transition-colors"
              data-testid="share-whatsapp-btn"
            >
              <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </div>
              <span className="text-white font-medium">WhatsApp</span>
            </button>
            
            {/* Download for manual sharing */}
            <div className="border-t border-slate-700 pt-4">
              <p className="text-slate-400 text-xs text-center mb-3">
                For Instagram, TikTok & Snapchat, download and share manually:
              </p>
              <Button
                onClick={downloadPhoto}
                variant="outline"
                className="w-full border-slate-600 text-white hover:bg-slate-700"
                data-testid="download-photo-btn"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Download Photo
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Before/After Compare View */}
      <Dialog open={showCompareView} onOpenChange={() => resetCompare()}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-4xl p-0 overflow-hidden">
          {comparePhotos.before && comparePhotos.after && (
            <div className="relative">
              {/* Header */}
              <div className="p-3 bg-slate-800 flex items-center justify-between border-b border-slate-700">
                <h3 className="text-white font-medium flex items-center gap-2">
                  <GitCompare className="h-5 w-5 text-purple-400" />
                  Before / After Comparison
                </h3>
                <button
                  onClick={resetCompare}
                  className="p-1 hover:bg-slate-700 rounded"
                >
                  <X className="h-5 w-5 text-slate-400" />
                </button>
              </div>
              
              {/* Comparison Container */}
              <div 
                ref={compareRef}
                className="relative w-full aspect-square max-h-[60vh] overflow-hidden cursor-ew-resize select-none"
                onMouseMove={(e) => e.buttons === 1 && handleSliderDrag(e)}
                onTouchMove={handleSliderDrag}
                data-testid="compare-container"
              >
                {/* After Image (Full, underneath) */}
                <img
                  src={comparePhotos.after.data}
                  alt="After"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                
                {/* Before Image (Clipped) */}
                <div 
                  className="absolute inset-0 overflow-hidden"
                  style={{ width: `${sliderPosition}%` }}
                >
                  <img
                    src={comparePhotos.before.data}
                    alt="Before"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ width: `${100 / (sliderPosition / 100)}%`, maxWidth: 'none' }}
                  />
                </div>
                
                {/* Slider Line */}
                <div 
                  className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-ew-resize"
                  style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
                >
                  {/* Slider Handle */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center">
                    <ChevronLeft className="h-4 w-4 text-slate-800 absolute left-1" />
                    <ChevronRight className="h-4 w-4 text-slate-800 absolute right-1" />
                  </div>
                </div>
                
                {/* Labels */}
                <div className="absolute top-3 left-3 bg-yellow-500 text-slate-900 px-2 py-1 rounded text-xs font-bold">
                  BEFORE
                </div>
                <div className="absolute top-3 right-3 bg-green-500 text-white px-2 py-1 rounded text-xs font-bold">
                  AFTER
                </div>
              </div>
              
              {/* Date Info */}
              <div className="p-4 bg-slate-800 grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-xs text-yellow-400 mb-1">BEFORE</p>
                  <p className="text-white text-sm">
                    {new Date(comparePhotos.before.date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                  {comparePhotos.before.note && (
                    <p className="text-slate-400 text-xs">{comparePhotos.before.note}</p>
                  )}
                </div>
                <div className="text-center">
                  <p className="text-xs text-green-400 mb-1">AFTER</p>
                  <p className="text-white text-sm">
                    {new Date(comparePhotos.after.date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                  {comparePhotos.after.note && (
                    <p className="text-slate-400 text-xs">{comparePhotos.after.note}</p>
                  )}
                </div>
              </div>
              
              {/* Instructions */}
              <div className="p-3 bg-slate-900 text-center">
                <p className="text-slate-400 text-xs">
                  👆 Drag the slider to compare your transformation
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
